import {apiHttpClient} from "@/http/httpClient";
import type {
    AssignmentListModel,
    AssignmentListRequest
} from "@/models/assignment/assignment-list";
import type{AssignmentDetailModel} from "@/models/assignment/assignment-detail";
import type {Assignment} from "@/models/assignment/assignment";
import type {PaginationItems} from "@/models/pagination";
import type {AssignmentRoutingModel} from "@/models/assignment/routing";
import type {AssignmentGeneralInformation} from "@/models/assignment/edit/general-information";
import type {AssignmentVehicleInformation} from "@/models/assignment/edit/vehicle-information";
import type {AssignRelationType} from "@/constants/assign-relation-type";
import {AssignmentStatus} from "@/constants/assignment-status";
import { AssignmentDrivability } from "@/constants/assignment-drivability";
import { AssignmentImportResponse } from "@/models/assignment/assignment-import";
import type { AssignmentOverlapInfo, AssignmentOverlapRequest } from "@/models/assignment/assignment-overlap";
import type { ExportLinesRequest } from "@/models/assignment/export-lines-request";
import type { ExportLinesResponse } from "@/models/assignment/export-lines-response";
import type { AssignmentExportTemplate, AssignmentExportTemplateResponse } from "@/models/assignment/assignment-export-template";
import type { AssignmentHistoryModel } from "@/models/assignment/assignment-history";
const ASSIGNMENT_BASE = 'Assignment';
const ENDPOINT_LIST_ASSIGNMENTS = `${ASSIGNMENT_BASE}/List`;
const ENDPOINT_GET_UPDATE_PROPOSAL_ORIGINAL = `${ASSIGNMENT_BASE}/GetUpdateProposalOriginal`;
const ENDPOINT_GET_ASSIGNMENT = `${ASSIGNMENT_BASE}/Get`;
const ENDPOINT_GET_ROUTING = `${ASSIGNMENT_BASE}/GetRouting`;
const ENDPOINT_GET_OVERLAPS = `${ASSIGNMENT_BASE}/GetOverlaps`;
const ENDPOINT_CREATE_ASSIGNMENT = `${ASSIGNMENT_BASE}/Create`;
const ENDPOINT_UPDATE_GENERAL_INFORMATION = `${ASSIGNMENT_BASE}/UpdateGeneralInformation`;
const ENDPOINT_UPDATE_VEHICLE_INFORMATION = `${ASSIGNMENT_BASE}/UpdateVehicleInformation`;
const ENDPOINT_UPDATE_RELATION_INFORMATION = `${ASSIGNMENT_BASE}/UpdateRelationInformation`;
const ENDPOINT_SWITCH_STORAGE_STATUS = `${ASSIGNMENT_BASE}/SwitchInStorageStatus`;
const ENDPOINT_GET_AVAILABLE_ASSIGNMENT_REASONS = `${ASSIGNMENT_BASE}/GetAvailableTransportReasons`;
const ENDPOINT_GET_AVAILABLE_ASSIGNMENT_REASONS_FOR_CURRENT = `${ASSIGNMENT_BASE}/GetAvailableTransportReasonsForCurrentTransport`;
const ENDPOINT_GET_WAY_BILLS = `${ASSIGNMENT_BASE}/GetWayBills`;
const ENDPOINT_DOWNLOAD_WAY_BILL = `${ASSIGNMENT_BASE}/DownloadWayBill`;
const ENDPOINT_GET_WAY_BILLS_WITH_DAMAGE_IMAGES = `${ASSIGNMENT_BASE}/GetWayBillsWithDamageImages`;
const ENDPOINT_GET_AMOUNT_RECEIVED = `${ASSIGNMENT_BASE}/GetAmountReceived`;
const ENDPOINT_GET_AMOUNT_READY_FOR_PROMATIE = `${ASSIGNMENT_BASE}/GetAmountReadyForPromatie`;
const ENDPOINT_SEND_TO_PROMATIE = `${ASSIGNMENT_BASE}/SendToPromatie`;
const ENDPOINT_ACCEPT_RECEIVED = `${ASSIGNMENT_BASE}/AcceptReceived`;
const ENDPOINT_REFUSE_RECEIVED = `${ASSIGNMENT_BASE}/RefuseReceived`;
const ENDPOINT_CANCEL_ASSIGNMENT = `${ASSIGNMENT_BASE}/Cancel`;
const ENDPOINT_REMOVE_URGENCY = `${ASSIGNMENT_BASE}/RemoveUrgency`;
const ENDPOINT_IMPORT_ASSIGNMENTS = `${ASSIGNMENT_BASE}/Import`;
const ENDPOINT_EXPORT_LINES = `${ASSIGNMENT_BASE}/ExportLines`;
const ENDPOINT_GET_EXPORT_TEMPLATES_LIST = `${ASSIGNMENT_BASE}/GetExportTemplatesList`;
const ENDPOINT_GET_EXPORT_TEMPLATE = `${ASSIGNMENT_BASE}/GetExportTemplate`;
const ENDPOINT_DELETE_EXPORT_TEMPLATE = `${ASSIGNMENT_BASE}/DeleteExportTemplate`;
const ENDPOINT_GET_HISTORY = `${ASSIGNMENT_BASE}/GetHistory`;
const ENDPOINT_RECALCULATE_REVENUE = `${ASSIGNMENT_BASE}/RecalculateRevenue`;

const mapAssignmentStatusToAssignmentStatus = (status: AssignmentStatus): AssignmentStatus => {
    switch (status) {
        case AssignmentStatus.ToPlan:
            return AssignmentStatus.ToPlan;
        case AssignmentStatus.Planned:
            return AssignmentStatus.Planned;
        case AssignmentStatus.InTransit:
            return AssignmentStatus.InTransit;
        case AssignmentStatus.Completed:
            return AssignmentStatus.Completed;
        case AssignmentStatus.JourneyWaypoint:
            return AssignmentStatus.JourneyWaypoint;
        case AssignmentStatus.InStorage:
            return AssignmentStatus.InStorage;
        case AssignmentStatus.Received:
            return AssignmentStatus.Received;
        case AssignmentStatus.Cancelled:
            return AssignmentStatus.Cancelled;
        case AssignmentStatus.UpdateReceived:
            return AssignmentStatus.UpdateReceived;
        default:
            throw new Error(`Unknown AssignmentStatus: ${status}`);
    }
}

const mapAssignmentRequestToAssignmentRequest = (request: AssignmentListRequest): any => {
    return {
        ...request,
        statuses: request.statuses?.map(mapAssignmentStatusToAssignmentStatus)
    };
}

const mapAssignmentListToAssignmentList = (item: any): AssignmentListModel => {
    return {
        ...item,
        status: mapAssignmentStatusToAssignmentStatus(item.status),
        statusHistory: item.statusHistory?.map((h: any) => ({
            status: mapAssignmentStatusToAssignmentStatus(h.status),
            createdOn: h.createdOn
        }))
    };
}

export const getAssignments = async (request: AssignmentListRequest) => {
    const mappedRequest = mapAssignmentRequestToAssignmentRequest(request);
    
    return await apiHttpClient().post<PaginationItems<any>>(ENDPOINT_LIST_ASSIGNMENTS, mappedRequest)
        .then(response => {
            if (response.data && response.data.items) {
                response.data.items = response.data.items.map(mapAssignmentListToAssignmentList);
            }
            return response as any;
        })
        .catch(error => {
            return error;
        })
}

export const getAssignment = async (id: number) => {
    return await apiHttpClient().get<Assignment>(ENDPOINT_GET_ASSIGNMENT, {
        params: {
            id: id
        }
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const createAssignment = (request: AssignmentDetailModel) => {
    const mappedRequest = {
        ...request,
        vehicles: request.vehicles.map((vehicle) => ({
            ...vehicle,
            drivability: vehicle.drivability ?? AssignmentDrivability.Drivable,
            transportedByRoad: vehicle.transportedByRoad ?? false,
        })),
    };

    return apiHttpClient().post(ENDPOINT_CREATE_ASSIGNMENT, mappedRequest)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getAssignmentRouting = async (id: number) => {
    return await apiHttpClient().get<AssignmentRoutingModel>(ENDPOINT_GET_ROUTING, {
        params: {
            assignmentId: id
        }
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getAssignmentOverlaps = async (request: AssignmentOverlapRequest) => {
    return await apiHttpClient().post<AssignmentOverlapInfo[]>(ENDPOINT_GET_OVERLAPS, request)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const updateAssignmentGeneralInformation = async (generalInformation: AssignmentGeneralInformation, id: number) => {
    return await apiHttpClient().post(ENDPOINT_UPDATE_GENERAL_INFORMATION, {
        assignmentId: id,
        id: id,
        ...generalInformation
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const updateAssignmentVehicleInformation = async (vehicleInformation: AssignmentVehicleInformation, id: number, existingVehicleId?: number) => {
    return await apiHttpClient().post(ENDPOINT_UPDATE_VEHICLE_INFORMATION, {
        assignmentId: id,
        existingVehicleId: existingVehicleId ?? null,
        ...vehicleInformation
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const updateAssignmentRelationInformation = async (
    relationId: number,
    addressId: number,
    relationType: AssignRelationType,
    id: number,
    contactPersonName?: string,
    contactPersonEmail?: string,
    contactPersonPhone?: string,
) => {
    return await apiHttpClient().post(ENDPOINT_UPDATE_RELATION_INFORMATION, {
        assignmentId: id,
        relationId: relationId,
        addressId: addressId,
        relationType: relationType,
        contactPersonName,
        contactPersonEmail,
        contactPersonPhone,
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const switchAssignmentInStorageStatus = async (assignmentId: number) => {
    return await apiHttpClient().post(`${ENDPOINT_SWITCH_STORAGE_STATUS}/${assignmentId}`, {
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getAvailableAssignmentTransportReasons = async () => {
    return await apiHttpClient().get(`${ENDPOINT_GET_AVAILABLE_ASSIGNMENT_REASONS}`, {
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getAvailableTransportReasonsForCurrentAssignment = async (assignmentId: number) => {
    return await apiHttpClient().get(`${ENDPOINT_GET_AVAILABLE_ASSIGNMENT_REASONS_FOR_CURRENT}/${assignmentId}`, {
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getAssignmentWayBills = async (assignmentId: number) => {
    return await apiHttpClient().get(`${ENDPOINT_GET_WAY_BILLS}/${assignmentId}`, {
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const downloadAssignmentWayBill = async (wayBillId: number) => {
    return await apiHttpClient().get(`${ENDPOINT_DOWNLOAD_WAY_BILL}/${wayBillId}`);
}

export const getAssignmentWayBillsWithDamageImages = async (assignmentId: number) => {
    return await apiHttpClient().get(`${ENDPOINT_GET_WAY_BILLS_WITH_DAMAGE_IMAGES}/${assignmentId}`, {
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getAmountReceivedAssignments = async () => {
    return await apiHttpClient().get<number>(ENDPOINT_GET_AMOUNT_RECEIVED)
        .then(response => response)
        .catch(error => error);
};

export const getAmountReadyForPromatie = async () => {
    return await apiHttpClient().get<number>(ENDPOINT_GET_AMOUNT_READY_FOR_PROMATIE)
        .then(response => response)
        .catch(error => error);
};

// Zelfde vorm als het importresultaat: fouten en waarschuwingen per opdracht met een vaste reden-code
// die in de UI wordt vertaald (send_to_promatie_result_modal.reasons.*).
export interface SendToPromatieResultItem {
    assignmentId: number;
    reference?: string;
    reason: string;
    details?: string;
}

export interface SendToPromatieResponse {
    succeededCount: number;
    assignmentIds: number[];
    errors: SendToPromatieResultItem[];
    warnings: SendToPromatieResultItem[];
}

// handleError: false — een batch wordt in delen verstuurd en de component toont zelf het resultaat,
// net als bij de import; de globale foutafhandeling zou daar doorheen praten.
export const sendToPromatie = async (assignmentIds: number[]) => {
    return await apiHttpClient(false).post<SendToPromatieResponse>(ENDPOINT_SEND_TO_PROMATIE, {
        assignmentIds: assignmentIds
    })
        .then(response => response)
        .catch(error => error);
};

export interface RecalculateRevenueResponse {
    totalRevenue: number;
    revenueOverride: number | null;
    promatieErrorMessage: string | null;
}

// Admin-only in the UI; re-fetches a single assignment's revenue from Promatie and persists it.
export const recalculateRevenue = async (assignmentId: number) => {
    return await apiHttpClient().post<RecalculateRevenueResponse>(`${ENDPOINT_RECALCULATE_REVENUE}/${assignmentId}`, {})
        .then(response => response)
        .catch(error => error);
};

export const acceptReceivedAssignments = async (assignmentIds: number[]) => {
    return await apiHttpClient().put(ENDPOINT_ACCEPT_RECEIVED, {
        assignmentIds: assignmentIds
    })
        .then(response => response)
        .catch(error => error);
}

export const refuseReceivedAssignments = async (assignmentIds: number[]) => {
    return await apiHttpClient().put(ENDPOINT_REFUSE_RECEIVED, {
        assignmentIds: assignmentIds
    })
        .then(response => response)
        .catch(error => error);
}

export const cancelAssignment = async (assignmentId: number) => {
    return await apiHttpClient().post(`${ENDPOINT_CANCEL_ASSIGNMENT}/${assignmentId}`, {})
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const removeAssignmentUrgency = async (assignmentId: number) => {
    return await apiHttpClient().post(`${ENDPOINT_REMOVE_URGENCY}/${assignmentId}`, {})
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const importAssignments = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    return await apiHttpClient(false).post<AssignmentImportResponse>(ENDPOINT_IMPORT_ASSIGNMENTS, formData)
      .then(response => {
        return response;
      })
      .catch(error => {
        return error;
      })
}

export const exportAssignmentLines = (request: ExportLinesRequest) => {
    return apiHttpClient()
        .post<ExportLinesResponse>(ENDPOINT_EXPORT_LINES, request)
        .then(response => response)
        .catch(error => error);
}

export const getAssignmentExportTemplatesList = () => {
    return apiHttpClient()
        .get<AssignmentExportTemplate[]>(ENDPOINT_GET_EXPORT_TEMPLATES_LIST)
        .then(response => response)
        .catch(error => error);
}

export const getAssignmentExportTemplate = (id: number) => {
    return apiHttpClient()
        .get<AssignmentExportTemplateResponse>(`${ENDPOINT_GET_EXPORT_TEMPLATE}/${id}`)
        .then(response => response)
        .catch(error => error);
}

export const deleteAssignmentExportTemplate = (id: number) => {
    return apiHttpClient()
        .delete(`${ENDPOINT_DELETE_EXPORT_TEMPLATE}/${id}`)
        .then(response => response)
        .catch(error => error);
}

export const getAssignmentHistory = async (assignmentId: number) => {
    return await apiHttpClient()
        .get<AssignmentHistoryModel[]>(ENDPOINT_GET_HISTORY, { params: { assignmentId } })
        .then(response => response)
        .catch(error => error);
}

export const getUpdateProposalOriginal = async (proposalId: number) => {
    return await apiHttpClient()
        .get<AssignmentListModel>(`${ENDPOINT_GET_UPDATE_PROPOSAL_ORIGINAL}/${proposalId}`)
        .then(response => response)
        .catch(error => error);
}
